import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDebug } from '../contexts/DebugContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { can } from '../utils/permissions';
import { moneyWhole as money, hours as qty } from '../utils/format';
import { displayDate as fmtDate } from '../utils/dates';
import { Banner, ExportButton, FilterButtons, IconButton, ICONS } from '../components/ui';
import { exportToExcel } from '../utils/exportExcel';

// Standalone "View Entries" window: actual Job Planning Line entries for a job or
// a single task line. Opened in a new browser tab from the Create Invoices entry
// screen (App renders this route without the app chrome). Defaults to Unbilled.

// Amounts drop .00 on whole dollars (moneyWhole); quantities up to 2 decimals;
// dates are the app's standard MM/DD/YYYY.

// Sortable columns -> value type (drives the comparator and default direction).
const SORT_TYPE = {
  PlanningDate: 'date', JobNo: 'str', JobTaskNo: 'str', No: 'str', Quantity: 'num',
  UnitPrice: 'num', TotalPrice: 'num', Description: 'str', Notes: 'str',
  NonBillable: 'num', Status: 'str', InvoiceNo: 'str',
};

// Local YYYY-MM-DD for a Date.
function ymd(d) {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
// Default resource-mode date range: the current month plus the two prior FULL
// calendar months — first day of the month two months back, through today.
function defaultFrom() {
  const d = new Date();
  return ymd(new Date(d.getFullYear(), d.getMonth() - 2, 1));
}
function defaultTo() {
  return ymd(new Date());
}

// A line that has not been transferred to any invoice can be edited (toggle
// non-billable, change job/task). Toggling non-billable is not allowed on Lump
// Sum task lines (JobTaskType 1), matching Page 1007.
// A line that hasn't been invoiced can be selected and moved — this includes
// Overhead lines (overhead time can be moved to another job/task).
// A line on a POSTED invoice cannot be changed (rate, billable flag) or moved. A
// line on an UNPOSTED invoice can: Navision takes it off the invoice first (and
// puts it back, or onto the new job's unposted invoice, as the action requires).
function isChangeable(l) { return !['Posted Invoice', 'Invoiced'].includes(l.Status); }
// The non-billable toggle is only for non-Lump-Sum, non-overhead not-yet-invoiced lines.
function canToggleNonBillable(l) { return isChangeable(l) && Number(l.JobTaskType) !== 1 && !Number(l.IsOverhead); }
// The rate (Work Type Code) can be changed on a T&M resource line that is not on
// a POSTED invoice (a line on an unposted invoice is taken off, changed and put
// back by Navision).
function canChangeRate(l) {
  return Number(l.JobTaskType) === 2 && Number(l.EntryTypeOption) === 0 && !Number(l.IsOverhead) && isChangeable(l);
}
// Stable per-line identity: Job Planning Line No. is unique only per
// (Job No., Job Task No., Line No.), so include all three (the Job No. matters in
// the cross-job resource view).
function lineKey(l) { return `${l.JobNo}|${l.JobTaskNo}|${l.LineNo}`; }

const th = { textAlign: 'left', padding: '6px 10px', fontSize: 12, color: 'var(--gray-600)', fontWeight: 600, borderBottom: '2px solid var(--gray-200)', position: 'sticky', top: 0, background: 'var(--card-bg, #fff)' };
const thR = { ...th, textAlign: 'right' };
const td = { padding: '6px 10px', fontSize: 13, verticalAlign: 'top', borderBottom: '1px solid var(--gray-100)' };
const tdR = { ...td, textAlign: 'right' };
const linkBtn = { background: 'none', border: 'none', color: 'var(--primary, #2563eb)', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' };

function statusPill(status) {
  const map = {
    Unbilled: { bg: '#fef9c3', fg: '#854d0e' },
    'Unposted Invoice': { bg: '#dbeafe', fg: '#1e40af' },
    'Posted Invoice': { bg: '#dcfce7', fg: '#166534' },
    Invoiced: { bg: '#e5e7eb', fg: '#374151' },
    'Non-Billable': { bg: '#f3f4f6', fg: '#6b7280' },
    Overhead: { bg: '#ede9fe', fg: '#5b21b6' },
  };
  const c = map[status] || { bg: '#f3f4f6', fg: '#374151' };
  return { fontSize: 11, background: c.bg, color: c.fg, borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap' };
}

// Modal: pick a new job / task for one or more planning lines, timesheet-style
// (searchable list of ALL jobs, not filtered by the user's job history). Reuses
// the same reference endpoints the timesheet job picker uses (getJobs/getJobTasks).
// `targets` is the array of lines being moved (1 for a single "Change job/task",
// N for a multi-select "Move selected").
function MoveJobModal({ targets, onCancel, onMove, busy }) {
  const line = (targets && targets[0]) || {};
  const single = targets && targets.length === 1;
  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [query, setQuery] = useState('');
  const [newJobNo, setNewJobNo] = useState('');
  const [newTaskNo, setNewTaskNo] = useState('');
  const [loadErr, setLoadErr] = useState('');

  useEffect(() => {
    let ok = true;
    api.getJobs()
      .then((j) => { if (ok) setJobs(Array.isArray(j) ? j : []); })
      .catch((e) => { if (ok) setLoadErr((e && e.message) || 'Failed to load jobs'); });
    return () => { ok = false; };
  }, []);

  // When a job is chosen, load its task lines (unfiltered).
  useEffect(() => {
    if (!newJobNo) { setTasks([]); setNewTaskNo(''); return; }
    let ok = true;
    api.getJobTasks(newJobNo)
      .then((t) => { if (ok) { setTasks(Array.isArray(t) ? t : []); setNewTaskNo(''); } })
      .catch((e) => { if (ok) setLoadErr((e && e.message) || 'Failed to load tasks'); });
    return () => { ok = false; };
  }, [newJobNo]);

  const q = query.trim().toLowerCase();
  const shownJobs = useMemo(() => {
    if (!q) return jobs.slice(0, 50);
    return jobs.filter((j) => `${j.JobID} ${j.JobName || ''} ${j.CustomerName || ''}`.toLowerCase().includes(q)).slice(0, 50);
  }, [jobs, q]);

  const canMove = newJobNo && newTaskNo && !busy &&
    !(single && newJobNo === line.JobNo && newTaskNo === (line.JobTaskNo || ''));

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '48px 16px' };
  const modal = { background: 'var(--card-bg, #fff)', borderRadius: 10, width: '100%', maxWidth: 520, padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.25)' };

  return (
    <div style={overlay} onClick={onCancel}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px' }}>Change job / task</h3>
        <div style={{ fontSize: 12, color: 'var(--gray-600)', marginBottom: 12 }}>
          {single
            ? <>Moving line {line.LineNo} — currently Job {line.JobNo}
                {line.JobTaskNo ? ` · Task ${line.JobTaskNo}` : ''}
                {line.PlanningDate ? ` · ${fmtDate(line.PlanningDate)}` : ''}</>
            : <>Moving {targets.length} selected lines.</>}
        </div>

        <Banner kind="err" onClose={() => setLoadErr('')}>{loadErr}</Banner>

        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-600)' }}>New job</label>
        <input
          autoFocus
          className="form-control"
          placeholder="Search jobs by number, name, or customer…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); }}
          style={{ width: '100%', margin: '4px 0 6px', padding: '6px 8px' }}
        />
        <div style={{ maxHeight: 190, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
          {shownJobs.length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--gray-600)' }}>No matching jobs.</div>
          ) : shownJobs.map((j) => (
            <div
              key={j.JobID}
              onClick={() => setNewJobNo(j.JobID)}
              style={{
                padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid var(--gray-100)',
                background: j.JobID === newJobNo ? 'var(--primary-soft, #eff6ff)' : 'transparent',
              }}
            >
              <span style={{ fontWeight: 500 }}>{j.JobID}</span> — {j.JobName}
              {j.CustomerName ? <span style={{ color: 'var(--gray-600)' }}> · {j.CustomerName}</span> : null}
            </div>
          ))}
        </div>

        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginTop: 12 }}>New task</label>
        <select
          className="form-control"
          value={newTaskNo}
          onChange={(e) => setNewTaskNo(e.target.value)}
          disabled={!newJobNo || !tasks.length}
          style={{ width: '100%', margin: '4px 0', padding: '6px 8px' }}
        >
          <option value="">{!newJobNo ? 'Select a job first…' : (tasks.length ? 'Select a task…' : 'No task lines')}</option>
          {tasks.map((t) => (
            <option key={t.JobTaskID} value={t.JobTaskID}>
              {t.JobTaskID} — {t.TaskCode} — {t.TaskName}
            </option>
          ))}
        </select>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="btn btn-outline btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={() => onMove(newJobNo, newTaskNo)} disabled={!canMove}>
            {busy ? 'Moving…' : (single ? 'Move line' : `Move ${targets.length} lines`)}
          </button>
        </div>
      </div>
    </div>
  );
}

// Number of receipts on an expense entry (ReceiptLinks: one "<url><TAB><description>"
// per line; '' for time entries and expenses without receipts).
const receiptCount = (l) => String(l.ReceiptLinks || '').split('\n').filter((x) => x.trim()).length;

export default function JobPlanningLinesView(props) {
  // inPage: the job page's Entries tab (the job page header names the job).
  // defaultFilter: 'unbilled' | 'invoiced' | 'all' (default: All on the job page
  // and for a person; Unbilled when opened from Create Invoices / Invoice Approval).
  const { job, task, embedded, inPage, defaultFilter, resource, resourceName, fromDate, toDate, onFullScreen, onClose } = props || {};
  const { debugMode } = useDebug();
  const { user } = useAuth();
  // Job/Task (job-scoped) or Resource (cross-job, from the Billable tab) come from
  // props when embedded (a modal), else from the URL (standalone /jpl route).
  const params = new URLSearchParams(window.location.search);
  const jobNo = (job !== undefined ? String(job) : (params.get('job') || '')).trim();
  const taskNo = (task !== undefined ? String(task) : (params.get('task') || '')).trim();
  const resourceNo = (resource !== undefined ? String(resource) : (params.get('resource') || '')).trim();
  const resourceLabel = (resourceName !== undefined ? String(resourceName) : (params.get('resourceName') || '')).trim();
  // Resource mode = a resource is given and we are NOT drilling into one job.
  const resourceMode = !jobNo && !!resourceNo;
  // Edit actions (toggle non-billable, change job/task, multi-select) are offered
  // the SAME way from both Create Invoices and the Billable tab — only to users
  // who have Navision Web Access (the same gate the write endpoints enforce).
  const canEdit = can(user, 'entries');
  const isReadOnly = !canEdit;
  const showJobCol = resourceMode; // show the Job column when not filtered to one job

  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fromD, setFromD] = useState(() => (fromDate || params.get('from') || defaultFrom()));
  const [toD, setToD] = useState(() => (toDate || params.get('to') || defaultTo()));
  // Billable (resource) view defaults to All; the Create Invoices (job) view to Unbilled.
  const [filter, setFilter] = useState(defaultFilter || (resourceMode || inPage ? 'all' : 'unbilled')); // 'unbilled' | 'invoiced' | 'all'
  // The job page first loads only recent entries (current month + the 3 before
  // it); "Load all" loads the rest. `since` is the first date loaded ('' = all).
  const [recentOnly, setRecentOnly] = useState(!!inPage && !resourceMode);
  const [since, setSince] = useState('');
  const [busyLine, setBusyLine] = useState(null); // lineKey currently being toggled
  const [actionMsg, setActionMsg] = useState(''); // feedback after a toggle/move
  const [actionErr, setActionErr] = useState(false); // style the feedback as an error
  const [moveTargets, setMoveTargets] = useState(null); // lines the move modal is open for
  const [moving, setMoving] = useState(false); // a move (single or multi) is in flight
  const [selected, setSelected] = useState(() => new Set()); // selected lineKeys (multi-move)
  const [sortKey, setSortKey] = useState('PlanningDate');
  const [sortDir, setSortDir] = useState('desc'); // default: newest date first
  const onSort = (key) => {
    if (key === sortKey) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); return; }
    setSortKey(key);
    setSortDir(SORT_TYPE[key] === 'str' ? 'asc' : 'desc'); // strings asc, dates/nums desc
  };

  // Open the invoice a line is on: the real PDF for a posted invoice (same
  // DocImage link the A/R Invoices tab uses), or a synthetic draft PDF for an
  // unposted one.
  const openInvoice = useCallback(async (l) => {
    const no = (l.InvoiceNo || '').toString().trim();
    if (!no) return;
    if (Number(l.InvoiceIsPosted)) {
      try {
        const r = await api.getInvoiceDocImage(no, debugMode);
        if (r && r.link) window.open(r.link, '_blank', 'noopener');
        else alert(`No posted invoice document (DocImage) is on file for invoice ${no}.`);
      } catch (e) {
        alert((e && ((e.responseBody && e.responseBody.error) || e.message)) || 'Failed to open the invoice document.');
      }
    } else {
      window.open(api.getSyntheticInvoicePdfUrl(no, l.JobNo), '_blank', 'noopener');
    }
  }, [debugMode]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = resourceMode
        ? await api.getResourcePlanningLines(resourceNo, fromD, toD, debugMode)
        : await api.getJobPlanningLines(jobNo, taskNo, debugMode, { recent: recentOnly });
      setLines(Array.isArray(resp.lines) ? resp.lines : []);
      setSince((!resourceMode && resp.since) || '');
    } catch (e) {
      setError((e && e.responseBody && e.responseBody.error) || (e && e.message) || 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  }, [resourceMode, resourceNo, fromD, toD, jobNo, taskNo, debugMode, recentOnly]);

  useEffect(() => {
    if (!embedded && !inPage) {
      document.title = resourceMode
        ? `${resourceLabel || resourceNo} — Entries`
        : `Job ${jobNo}${taskNo ? ` · Task ${taskNo}` : ''} — Entries`;
    }
    load();
  }, [load, jobNo, taskNo, resourceMode, resourceNo, resourceLabel, embedded, inPage]);

  // Toggle a line's Non-Billable flag (T&M / Expense / Charges lines only; the
  // server rejects Lump Sum and already-invoiced lines).
  const toggleNonBillable = useCallback(async (l) => {
    const nowNB = !Number(l.NonBillable);
    if (nowNB && l.Status === 'Unposted Invoice' && l.InvoiceNo
      && !window.confirm(`This line will be taken off unposted invoice ${l.InvoiceNo} (the invoice is deleted if this is its last line). Continue?`)) return;
    setBusyLine(lineKey(l));
    setActionMsg('');
    setActionErr(false);
    try {
      const r = await api.setPlanningLineNonBillable(l.JobNo, l.LineNo, l.JobTaskNo, nowNB);
      const who = (l.No || '').toString().trim() || 'This line';
      let msg = `${who} on ${fmtDate(l.PlanningDate)} is now ${nowNB ? 'non-billable' : 'billable'}.`;
      if (r && r.message) msg += ` ${r.message}`;
      // When it becomes non-billable and the current view hides non-billable
      // lines (any filter but "All"), it will drop off after refresh — say so.
      if (nowNB && filter !== 'all') {
        const label = filter === 'invoiced' ? 'Invoiced' : 'Unbilled';
        msg += ` This line no longer appears on the ${label} filter.`;
      }
      setActionMsg(msg);
      await load();
    } catch (e) {
      setActionMsg((e && e.responseBody && e.responseBody.error) || (e && e.message) || 'Failed to update the line.');
      setActionErr(true);
    } finally {
      setBusyLine(null);
    }
  }, [load, filter]);

  // Time rate codes by rate sheet, for the per-line rate picker.
  const [rateCodesBySheet, setRateCodesBySheet] = useState({});
  useEffect(() => {
    if (isReadOnly) return undefined;
    let alive = true;
    api.getRateCodes(undefined, 'Time').then((rows) => {
      if (!alive) return;
      const m = {};
      (Array.isArray(rows) ? rows : []).forEach((r) => { (m[r.RateSheetCode] = m[r.RateSheetCode] || []).push(r); });
      setRateCodesBySheet(m);
    }).catch(() => {});
    return () => { alive = false; };
  }, [isReadOnly]);

  // Rate (Work Type Code) edit: the pencil next to a line's Unit Price turns that
  // cell into the job's rate-sheet picker + save. The change applies to that line
  // AND every checked line with the same resource, rate code and rate sheet
  // (checked before or after starting the edit). Navision re-prices each line
  // (unit price, total, description) as Page 1007 does; a line on an unposted
  // invoice is taken off it, changed and put back on the same invoice.
  const [rateEdit, setRateEdit] = useState(null); // { key, code }
  const [savingRate, setSavingRate] = useState(false);
  const sameRateGroup = (a, b) => String(a.No || '').trim() === String(b.No || '').trim()
    && (a.WorkTypeCode || '') === (b.WorkTypeCode || '')
    && String(a.JobRateSheetCode || '').trim() === String(b.JobRateSheetCode || '').trim();
  const rateTargets = (row) => {
    const keys = new Set([lineKey(row)]);
    const out = [row];
    lines.forEach((l) => {
      if (selected.has(lineKey(l)) && !keys.has(lineKey(l)) && canChangeRate(l) && sameRateGroup(l, row)) {
        keys.add(lineKey(l));
        out.push(l);
      }
    });
    return out;
  };
  const saveRate = async (row, code) => {
    const targets = rateTargets(row);
    if (!code || code === (row.WorkTypeCode || '')) { setRateEdit(null); return; }
    const sheet = rateCodesBySheet[String(row.JobRateSheetCode || '').trim()] || [];
    const pick = sheet.find((r) => r.RateCodeID === code);
    const label = pick ? `${pick.RateCodeName} (${money(pick.Rate)}/hr)` : code;
    const invoices = Array.from(new Set(targets.filter((l) => l.Status === 'Unposted Invoice' && l.InvoiceNo).map((l) => l.InvoiceNo)));
    if (invoices.length && !window.confirm(`Change ${targets.length} line(s) to ${label}?`
      + `\n\nLines on unposted invoice ${invoices.join(', ')} will be taken off it, changed, and put back on it.`)) return;
    setSavingRate(true);
    setActionMsg('');
    setActionErr(false);
    let ok = 0;
    const errs = [];
    for (const l of targets) {
      try {
        await api.changePlanningLineWorkType(l.JobNo, l.LineNo, l.JobTaskNo, code);
        ok += 1;
      } catch (e) {
        errs.push(`${fmtDate(l.PlanningDate)}: ${(e && e.responseBody && e.responseBody.error) || (e && e.message) || 'failed'}`);
      }
    }
    const who = (row.No || '').toString().trim() || 'Line';
    setActionMsg(errs.length
      ? `${ok} of ${targets.length} line(s) changed to ${label}. Failed — ${errs.join('; ')}`
      : `${who}: ${ok} line(s) now billed at ${label}.${invoices.length ? ` Updated on invoice ${invoices.join(', ')}.` : ''}`);
    setActionErr(errs.length > 0);
    setRateEdit(null);
    setSelected(new Set());
    setSavingRate(false);
    await load();
  };

  // Move one or more lines to a new job / task (reuses Report 50000's UpdateJobNo
  // logic on the server, one call per line; the server rolls each line into an
  // existing unposted invoice when one matches the new job + customer). Lines are
  // moved sequentially so a failure on one does not abort the rest.
  const doMoveTargets = useCallback(async (newJobNo, newJobTaskNo) => {
    const targets = moveTargets || [];
    if (!targets.length) return;
    // Lines on an unposted invoice are taken off it before the move (Navision
    // deletes the invoice instead when its last line is moved off).
    const fromInvoices = Array.from(new Set(targets.filter((l) => l.Status === 'Unposted Invoice' && l.InvoiceNo).map((l) => l.InvoiceNo)));
    if (fromInvoices.length && !window.confirm(`Lines on unposted invoice ${fromInvoices.join(', ')} will be taken off it before moving `
      + '(an invoice is deleted if all of its lines are moved off). Continue?')) return;
    setMoving(true);
    setActionMsg('');
    setActionErr(false);
    let ok = 0;
    let lastMsg = '';
    const errs = [];
    for (const l of targets) {
      try {
        const r = await api.movePlanningLine(l.JobNo, l.LineNo, l.JobTaskNo, newJobNo, newJobTaskNo);
        ok += 1;
        lastMsg = (r && r.message) || '';
      } catch (e) {
        const who = (l.No || '').toString().trim() || `Line ${l.LineNo}`;
        errs.push(`${who} on ${fmtDate(l.PlanningDate)}: ${(e && e.responseBody && e.responseBody.error) || (e && e.message) || 'failed'}`);
      }
    }
    setMoveTargets(null);
    setSelected(new Set());
    let msg;
    let isErr = false;
    if (targets.length === 1) {
      if (ok === 1) {
        msg = lastMsg || `Line ${targets[0].LineNo} moved to Job ${newJobNo} · Task ${newJobTaskNo}.`;
      } else {
        msg = errs[0] || 'The move failed. No changes were made.';
        isErr = true;
      }
    } else {
      msg = `Moved ${ok} of ${targets.length} lines to Job ${newJobNo} · Task ${newJobTaskNo}.`;
      if (errs.length) { msg += ` ${errs.length} failed — ${errs.join('; ')}`; isErr = true; }
    }
    setActionMsg(msg);
    setActionErr(isErr);
    await load();
    setMoving(false);
  }, [moveTargets, load]);

  const visible = useMemo(() => {
    let rows;
    if (filter === 'all') rows = lines;
    else if (filter === 'invoiced') rows = lines.filter((l) => l.Status === 'Invoiced' || l.Status === 'Posted Invoice' || l.Status === 'Unposted Invoice');
    else rows = lines.filter((l) => l.Status === 'Unbilled'); // default
    // A job view opened for one person (e.g. from the Dashboard rate exceptions)
    // shows only that resource's lines.
    if (!resourceMode && resourceNo) rows = rows.filter((l) => String(l.No || '').trim() === resourceNo);
    // Sort by the clicked column; default is newest date first.
    const type = SORT_TYPE[sortKey] || 'str';
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let d;
      if (type === 'num') d = (parseFloat(a[sortKey]) || 0) - (parseFloat(b[sortKey]) || 0);
      else if (type === 'date') d = new Date(a[sortKey] || 0) - new Date(b[sortKey] || 0);
      else d = String(a[sortKey] || '').localeCompare(String(b[sortKey] || ''));
      if (d !== 0) return d * dir;
      // Stable secondary: newest date, then line no.
      return (new Date(b.PlanningDate || 0) - new Date(a.PlanningDate || 0)) || ((Number(a.LineNo) || 0) - (Number(b.LineNo) || 0));
    });
  }, [lines, filter, sortKey, sortDir, resourceMode, resourceNo]);

  // Export the entries as shown (filter + sort) to Excel.
  const exportEntries = () => exportToExcel({
    name: 'Entries',
    parts: resourceMode ? [resourceNo] : [jobNo, taskNo],
    title: resourceMode ? `Entries — ${resourceLabel || resourceNo}` : `Entries — Job ${jobNo}${taskNo ? ` · Task ${taskNo}` : ''}`,
    columns: [
      { header: 'Date', key: 'PlanningDate', type: 'date' },
      { header: 'Job', key: 'JobNo' },
      { header: 'Task', key: 'JobTaskNo' },
      { header: 'Type', key: 'EntryType' },
      { header: 'Resource / Account', key: 'No' },
      { header: 'Employee', key: 'ResourceName' },
      { header: 'Description', key: 'Description' },
      { header: 'Notes', key: 'Notes' },
      { header: 'Qty', key: 'Quantity', type: 'hours' },
      { header: 'Unit Price', key: 'UnitPrice', type: 'money' },
      { header: 'Total Price', key: 'TotalPrice', type: 'money' },
      { header: 'Non-Billable', key: 'NonBillableText' },
      { header: 'Status', key: 'Status' },
      { header: 'Invoice', key: 'InvoiceNo' },
    ],
    rows: visible.map((l) => ({ ...l, NonBillableText: Number(l.NonBillable) ? 'Yes' : 'No' })),
  });

  const totals = useMemo(() => visible.reduce((a, l) => {
    a.qty += parseFloat(l.Quantity) || 0;
    a.total += parseFloat(l.TotalPrice) || 0;
    return a;
  }, { qty: 0, total: 0 }), [visible]);

  // Multi-select (for "Move selected" and a multi-line rate edit): any line not on
  // a posted invoice.
  const isSelectable = isChangeable;
  const eligibleVisible = useMemo(() => visible.filter(isChangeable), [visible]);
  const selectedLines = useMemo(() => lines.filter((l) => selected.has(lineKey(l)) && isChangeable(l)), [lines, selected]);
  const allEligibleSelected = eligibleVisible.length > 0 && eligibleVisible.every((l) => selected.has(lineKey(l)));
  const toggleSelect = (l) => setSelected((s) => {
    const n = new Set(s); const k = lineKey(l);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const setAllEligible = (checked) => setSelected(() => {
    const n = new Set();
    if (checked) eligibleVisible.forEach((l) => n.add(lineKey(l)));
    return n;
  });

  // Clickable, sortable column header.
  const sortTh = (key, label, align) => {
    const base = align === 'r' ? thR : th;
    const active = sortKey === key;
    return (
      <th style={{ ...base, cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort(key)} title="Click to sort">
        {label}{active ? ` ${sortDir === 'asc' ? ICONS.sortUp : ICONS.sortDown}` : ''}
      </th>
    );
  };

  // Compact, always-visible header: title + full-screen + "Move selected" + close.
  // In embedded (modal) mode this bar stays fixed while the body below scrolls, so
  // the title and the Move-selected action remain visible as the list scrolls.
  const headerBar = (
    <div style={embedded
      ? { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderBottom: '1px solid var(--gray-200)', flex: '0 0 auto' }
      : { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '4px 0' }}>
      {onFullScreen && (
        <IconButton icon="external" title="Open full screen in a new window" onClick={onFullScreen} style={{ flex: '0 0 auto' }} />
      )}
      <h2 style={{
        margin: 0, fontSize: embedded ? 15 : undefined, fontWeight: 600,
        flex: '1 1 auto', minWidth: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {inPage ? null : resourceMode
          ? <>Entries — {resourceLabel || resourceNo}</>
          : <>Entries — Job {jobNo}{taskNo ? ` · Task ${taskNo}` : ' · (all task lines)'}{resourceNo ? ` · ${resourceLabel || resourceNo}` : ''}</>}
      </h2>
      {!isReadOnly && selectedLines.length > 0 && (
        <button className="btn btn-primary btn-sm" disabled={moving} style={{ flex: '0 0 auto' }}
          onClick={() => { setActionMsg(''); setMoveTargets(selectedLines); }}>
          Move selected ({selectedLines.length})
        </button>
      )}
      {onClose && <IconButton icon="close" title="Close" onClick={onClose} style={{ flex: '0 0 auto' }} />}
    </div>
  );

  return (
    <div style={embedded
      ? { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }
      : { maxWidth: 1100, margin: '0 auto', padding: '16px 16px 48px' }}>
      {headerBar}
      <div style={embedded ? { flex: '1 1 auto', overflow: 'auto', minHeight: 0, padding: '0 12px 16px' } : undefined}>
      {resourceMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '6px 0' }}>
          <label style={{ fontSize: 13, color: 'var(--gray-600)' }}>
            From <input type="date" className="form-control" value={fromD} onChange={(e) => setFromD(e.target.value)} style={{ width: 150, display: 'inline-block' }} />
          </label>
          <label style={{ fontSize: 13, color: 'var(--gray-600)' }}>
            To <input type="date" className="form-control" value={toD} onChange={(e) => setToD(e.target.value)} style={{ width: 150, display: 'inline-block' }} />
          </label>
          <button className="btn btn-primary btn-sm" onClick={load} disabled={loading}>Apply dates</button>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '10px 0' }}>
        <FilterButtons value={filter} onChange={setFilter}
          options={[{ key: 'unbilled', label: 'Unbilled' }, { key: 'invoiced', label: 'Invoiced' }, { key: 'all', label: 'All' }]} />
        <IconButton icon="refresh" title="Refresh" onClick={load} disabled={loading} />
        <ExportButton onExport={exportEntries} disabled={loading || !visible.length} />
        {since && (
          <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>
            Since {fmtDate(since)}
            <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 6 }} disabled={loading}
              onClick={() => setRecentOnly(false)}>Load all</button>
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--gray-600)' }}>
          {visible.length} {visible.length === 1 ? 'entry' : 'entries'} · {qty(totals.qty)} hrs · {money(totals.total)}
        </span>
      </div>

      <Banner kind="err">{error}</Banner>
      <Banner kind={actionErr ? 'err' : 'info'} onClose={() => { setActionMsg(''); setActionErr(false); }}>{actionMsg}</Banner>

      {loading ? (
        <div style={{ padding: 16 }}>Loading entries…</div>
      ) : !visible.length ? (
        <div style={{ padding: 16, color: 'var(--gray-600)' }}>No {filter === 'all' ? '' : filter} entries for this {resourceMode ? 'person' : (taskNo ? 'task line' : 'job')}.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {!isReadOnly && (
                <th style={th} title="Select lines (to move, or to change their rate together)">
                  <input type="checkbox" checked={allEligibleSelected} disabled={!eligibleVisible.length || moving}
                    onChange={(e) => setAllEligible(e.target.checked)} />
                </th>
              )}
              {sortTh('PlanningDate', 'Date')}
              {showJobCol
                ? sortTh('JobNo', 'Job / Task')
                : (!taskNo && sortTh('JobTaskNo', 'Task'))}
              {sortTh('No', 'Resource / Account')}
              {sortTh('Quantity', 'Qty', 'r')}
              {sortTh('UnitPrice', 'Unit Price', 'r')}
              {sortTh('TotalPrice', 'Total Price', 'r')}
              {sortTh('Description', 'Description')}
              {sortTh('Notes', 'Notes')}
              {sortTh('NonBillable', 'Non-Billable')}
              {sortTh('Status', 'Status')}
              {sortTh('InvoiceNo', 'Invoice')}
              {!isReadOnly && <th style={th}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {visible.map((l, i) => (
              <tr key={`${lineKey(l)}|${i}`}
                style={selected.has(lineKey(l)) ? { background: 'var(--primary-soft, #eff6ff)' } : undefined}>
                {!isReadOnly && (
                  <td style={td}>
                    {isSelectable(l) && (
                      <input type="checkbox" checked={selected.has(lineKey(l))} disabled={moving || savingRate}
                        onChange={() => toggleSelect(l)} />
                    )}
                  </td>
                )}
                <td style={td}>{fmtDate(l.PlanningDate)}</td>
                {showJobCol
                  ? <td style={td} title={`${l.JobNo}${l.JobDescription ? ` — ${l.JobDescription}` : ''}\n${l.JobTaskNo}${l.JobTaskDescription ? ` — ${l.JobTaskDescription}` : ''}`}>{l.JobNo}/{l.JobTaskNo}</td>
                  : (!taskNo && <td style={td} title={l.JobTaskDescription || ''}>{l.JobTaskNo}</td>)}
                <td style={td}>{l.No}</td>
                <td style={tdR}>{qty(l.Quantity)}</td>
                <td style={{ ...tdR, whiteSpace: 'nowrap' }}>
                  {(() => {
                    const sheet = rateCodesBySheet[String(l.JobRateSheetCode || '').trim()] || [];
                    const editable = !isReadOnly && canChangeRate(l) && sheet.length > 0;
                    if (rateEdit && rateEdit.key === lineKey(l)) {
                      const hasCurrent = sheet.some((r) => r.RateCodeID === l.WorkTypeCode);
                      const n = rateTargets(l).length;
                      return (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <select value={rateEdit.code} disabled={savingRate} autoFocus
                            onChange={(e) => setRateEdit({ key: rateEdit.key, code: e.target.value })}
                            style={{ fontSize: 12, padding: '1px 2px', maxWidth: 200 }}>
                            {!hasCurrent && <option value={l.WorkTypeCode || ''}>{l.WorkTypeCode || '(none)'}</option>}
                            {sheet.map((r) => <option key={r.RateCodeID} value={r.RateCodeID}>{r.RateCodeName} ({money(r.Rate)})</option>)}
                          </select>
                          <IconButton icon={savingRate ? '…' : 'save'} disabled={savingRate}
                            title={n > 1 ? `Save the new rate on ${n} lines` : 'Save the new rate'}
                            onClick={() => saveRate(l, rateEdit.code)} />
                          <IconButton icon="cancel" title="Cancel" disabled={savingRate} onClick={() => setRateEdit(null)} />
                          {n > 1 && <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{n} lines</span>}
                        </span>
                      );
                    }
                    return (
                      <>
                        {money(l.UnitPrice)}
                        {editable && (
                          <IconButton icon="edit" style={{ marginLeft: 4 }} disabled={savingRate || !!rateEdit}
                            title={`Change the rate (${l.WorkTypeCode || 'none'})`}
                            onClick={() => setRateEdit({ key: lineKey(l), code: l.WorkTypeCode || '' })} />
                        )}
                      </>
                    );
                  })()}
                </td>
                <td style={tdR}>{money(l.TotalPrice)}</td>
                <td style={td}>
                  {l.Description}
                  {canEdit && receiptCount(l) > 0 && (
                    <a className="invoice-extra" style={{ marginLeft: 4 }} target="_blank" rel="noopener noreferrer"
                      href={api.planningLineReceiptsPdfUrl(l.JobNo, l.JobTaskNo, l.LineNo, l.InvoiceNo, !!Number(l.InvoiceIsPosted))}
                      title={`Receipts for this expense (${receiptCount(l)})`} aria-label="Receipts for this expense">
                      {ICONS.receipts}
                    </a>
                  )}
                </td>
                <td style={td}>{l.Notes}</td>
                <td style={td}>
                  {Number(l.IsOverhead) ? (
                    '' /* overhead jobs: billable/non-billable is not meaningful */
                  ) : !isReadOnly && canToggleNonBillable(l) ? (
                    <button type="button" style={linkBtn} disabled={busyLine === lineKey(l)}
                      onClick={() => toggleNonBillable(l)}
                      title={Number(l.NonBillable) ? 'Make this line billable' : 'Make this line non-billable'}>
                      {Number(l.NonBillable) ? 'Yes' : 'No'}
                    </button>
                  ) : (
                    <span style={{ color: 'var(--gray-600)' }}>{Number(l.NonBillable) ? 'Yes' : 'No'}</span>
                  )}
                </td>
                <td style={td}><span style={statusPill(l.Status)}>{l.Status}</span></td>
                <td style={{ ...td, whiteSpace: 'nowrap' }} title={parseFloat(l.InvoicedAmount) ? money(l.InvoicedAmount) : ''}>
                  {l.InvoiceNo
                    ? <button type="button" style={linkBtn} onClick={() => openInvoice(l)}
                        title={Number(l.InvoiceIsPosted) ? 'Open posted invoice PDF' : 'Open draft (unposted) invoice PDF'}>
                        {l.InvoiceNo}
                      </button>
                    : ''}
                </td>
                {!isReadOnly && (
                  <td style={td}>
                    {isChangeable(l) ? (
                      <button type="button" style={linkBtn} disabled={moving}
                        onClick={() => { setActionMsg(''); setMoveTargets([l]); }}
                        title="Move this line to a different job / task">
                        Move
                      </button>
                    ) : ''}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </div>

      {!isReadOnly && moveTargets && moveTargets.length > 0 && (
        <MoveJobModal
          targets={moveTargets}
          busy={moving}
          onCancel={() => { if (!moving) setMoveTargets(null); }}
          onMove={(nj, nt) => doMoveTargets(nj, nt)}
        />
      )}
    </div>
  );
}
